import strategyWorkflowService from "./StrategyWorkflowService";
import { QUICK_PLAN_FEATURES } from "../shared/productFoundationContract";

class WorkflowRegistry {
  _featureWorkflowServices = null;

  constructor() {
    this.workflows = new Map();
    this.registerDefaultWorkflows();
  }

  registerDefaultWorkflows() {
    // Autonomous Design Strategy Workflow
    this.registerWorkflow("autonomous_design_strategy", {
      name: "Autonomous Design Strategy",
      description:
        "AI-driven contextual program creation without CSV constraints",
      service: strategyWorkflowService, // Will use enhanced service
      requiredData: [
        "designChallenge",
        "contextualRequirements", // More flexible than fixed CSV data
      ],
      agents: ["DESIGN_LEAD", "UX_RESEARCH", "UX_DESIGN", "PROJECT_MANAGER"],
      estimatedDuration: "5-8 minutes",
      uiComponents: {
        views: [
          "pulse",
          "decisions",
          "strategy",
          "productStrategy",
          "competitor",
          "designAuthority",
          "stepByStep",
          "slick",
          "calendar",
          "readme",
        ],
        defaultView: "pulse",
        enabledFeatures: [
          "pulse_view",
          "decisions_view",
          "strategy_view",
          "product_strategy_view",
          "step_by_step_view",
          "competitor_analysis_view",
          "design_authority",
          "slick_view",
          "calendar_view",
          "readme_view",
        ],
        autoRunFeatures: [
          "strategy_view",
          "product_strategy_view",
          "step_by_step_view",
        ],
        chatPanel: {
          type: "autonomous_workflow_executor",
          showProgress: true,
          allowInteraction: true,
          autonomousMode: true,
        },
        navbar: {
          showSaveButton: true,
          showViewTabs: true,
          showDurationTag: true,
          autonomousIndicator: true, // Show this is autonomous mode
        },
      },
    });

    this.registerWorkflow("quick_plan", {
      name: "Quick Strategy",
      description:
        "Fast strategic planning focused on goals, objectives, and deliverables",
      service: strategyWorkflowService, // Will use enhanced service
      requiredData: [
        "designChallenge",
        "contextualRequirements", // More flexible than fixed CSV data
      ],
      agents: ["DESIGN_LEAD", "UX_RESEARCH", "UX_DESIGN", "PROJECT_MANAGER"],
      estimatedDuration: "1-2 minutes",
      uiComponents: {
        views: [
          "pulse",
          "decisions",
          "strategy",
          "productStrategy",
          "competitor",
          "designAuthority",
          "readme",
        ],
        defaultView: "pulse",
        enabledFeatures: [...QUICK_PLAN_FEATURES],
        autoRunFeatures: ["strategy_view", "product_strategy_view"],
        chatPanel: {
          type: "autonomous_workflow_executor",
          showProgress: true,
          allowInteraction: true,
          autonomousMode: true,
        },
        navbar: {
          showSaveButton: true,
          showViewTabs: true,
          showDurationTag: true,
          autonomousIndicator: true, // Show this is autonomous mode
        },
      },
    });

    // Feature Enablement Workflow - for manually adding features via + button
    // This is SEPARATE from agent-proposed feature enablement during conversation
    this.registerWorkflow("feature_enablement", {
      name: "Feature Enablement",
      description:
        "Generate content for a specific feature being manually enabled",
      service: strategyWorkflowService,
      requiredData: ["featureId", "planContext"],
      agents: ["DESIGN_LEAD"],
      estimatedDuration: "1-2 minutes",
      isFeatureEnablement: true, // Flag to identify this workflow type
      uiComponents: {
        chatPanel: {
          type: "feature_enablement_executor",
          showProgress: true,
          allowInteraction: false,
          autonomousMode: true,
          continueConversation: true, // Don't start new chat
        },
        navbar: {
          showSaveButton: false,
          autonomousIndicator: true,
        },
      },
    });
  }

  // Cache for fetched templates
  _templateCache = new Map();

  // Fetch feature enablement templates from API (dynamic, not hardcoded)
  async fetchFeatureEnablementTemplates(featureId) {
    // Check cache first
    if (this._templateCache.has(featureId)) {
      return this._templateCache.get(featureId);
    }

    try {
      const response = await fetch(
        `/api/core-features-registry/feature-enablement/${featureId}`,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch templates for ${featureId}`);
      }
      const data = await response.json();

      // Cache the result
      this._templateCache.set(featureId, data);
      return data;
    } catch (error) {
      console.warn(
        `⚠️ Failed to fetch templates for ${featureId}, using fallback:`,
        error,
      );
      // Fallback to generic template
      const fallback = this._generateFallbackTemplate(featureId);
      this._templateCache.set(featureId, fallback);
      return fallback;
    }
  }

  // Synchronous getter that returns cached template or fallback
  // Use fetchFeatureEnablementTemplates for async fetching
  getFeatureEnablementTemplates(featureId) {
    if (this._templateCache.has(featureId)) {
      return this._templateCache.get(featureId);
    }
    return this._generateFallbackTemplate(featureId);
  }

  // Generate a fallback template when API is unavailable
  _generateFallbackTemplate(featureId) {
    const name = featureId
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return {
      featureId,
      featureName: name,
      mode: "view", // Default to view-only for safety
      intro: `✨ Enabling ${name}...`,
      steps: [],
      completion: `✅ **${name} Enabled!** This view is now available in your navigation tabs.`,
      viewOnlyMessage: `✅ **${name} Enabled!** This view is now available in your navigation tabs.`,
    };
  }

  // Register a new workflow
  registerWorkflow(id, config) {
    this.workflows.set(id, {
      id,
      ...config,
      registered: new Date(),
    });
  }

  // Get workflow by ID
  getWorkflow(id) {
    return this.workflows.get(id);
  }

  // Get all available workflows
  getAllWorkflows() {
    return Array.from(this.workflows.values());
  }

  // Get workflows by agent type
  getWorkflowsByAgent(agentType) {
    return Array.from(this.workflows.values()).filter((workflow) =>
      workflow.agents.includes(agentType),
    );
  }

  // Get UI configuration for a workflow
  getUIConfig(workflowId) {
    const workflow = this.getWorkflow(workflowId);
    return workflow ? workflow.uiComponents : null;
  }

  // Get workflow for context - simple logic
  getWorkflowForContext(isAutonomous = false) {
    return isAutonomous ? "autonomous_design_strategy" : "design_strategy";
  }

  // Execute a workflow (deprecated - use StrategyWorkflowManager instead)
  async executeWorkflow(workflowId, params, callbacks) {
    console.warn(
      "⚠️ WorkflowRegistry.executeWorkflow is deprecated. Use StrategyWorkflowManager instead.",
    );

    const workflow = this.getWorkflow(workflowId);

    if (!workflow) {
      throw new Error(`Workflow ${workflowId} not found`);
    }

    // Validate required data
    const missingData = workflow.requiredData.filter((key) => !(key in params));
    if (missingData.length > 0) {
      throw new Error(`Missing required data: ${missingData.join(", ")}`);
    }

    // Execute the workflow using its service
    if (workflowId === "design_strategy") {
      return await workflow.service.executeWorkflow(
        params.designChallenge,
        params.programsOfWork,
        params.methods,
        params.tools,
        params.deliverables,
        callbacks,
      );
    } else {
      // For future workflows, use the custom workflow execution
      return await workflow.service.executeCustomWorkflow(workflowId, params);
    }
  }

  // Remove a workflow
  unregisterWorkflow(id) {
    return this.workflows.delete(id);
  }

  // Lazy getter for feature workflow services to avoid circular dependency
  // Note: BriefWorkflowService and ActionPlanWorkflowService export classes
  // ProductStrategyWorkflowService and CompetitorAnalysisWorkflowService export singleton instances
  get featureWorkflowServices() {
    if (!this._featureWorkflowServices) {
      const BriefWorkflowService = require("./BriefWorkflowService").default;
      const ActionPlanWorkflowService =
        require("./ActionPlanWorkflowService").default;
      // These are already singleton instances, not classes
      const productStrategyService =
        require("./ProductStrategyWorkflowService").default;
      const competitorAnalysisService =
        require("./CompetitorAnalysisWorkflowService").default;

      console.log("📦 WorkflowRegistry: Loading feature workflow services");
      console.log("  - BriefWorkflowService:", typeof BriefWorkflowService);
      console.log(
        "  - ActionPlanWorkflowService:",
        typeof ActionPlanWorkflowService,
      );
      console.log("  - productStrategyService:", typeof productStrategyService);
      console.log(
        "  - competitorAnalysisService:",
        typeof competitorAnalysisService,
      );

      this._featureWorkflowServices = {
        brief: new BriefWorkflowService(),
        action_plan: new ActionPlanWorkflowService(),
        product_strategy: productStrategyService, // Already an instance
        competitor_analysis: competitorAnalysisService, // Already an instance
      };
    }
    return this._featureWorkflowServices;
  }

  // Get feature workflow service by feature ID
  getFeatureWorkflowService(featureId) {
    const mapping = {
      strategy_view: "brief",
      brief: "brief",
      step_by_step_view: "action_plan",
      action_plan: "action_plan",
      product_strategy_view: "product_strategy",
      product_strategy: "product_strategy",
      competitor_analysis_view: "competitor_analysis",
      competitor_analysis: "competitor_analysis",
    };

    const serviceKey = mapping[featureId] || featureId;
    return this.featureWorkflowServices[serviceKey] || null;
  }

  // Get all feature workflow services for a product
  getProductFeatureWorkflows(workflowId) {
    const workflow = this.getWorkflow(workflowId);
    if (!workflow) return [];

    const services = this.featureWorkflowServices;
    const featureToWorkflowMapping = {
      strategy_view: { featureId: "brief", service: services.brief },
      step_by_step_view: {
        featureId: "action_plan",
        service: services.action_plan,
      },
      product_strategy_view: {
        featureId: "product_strategy",
        service: services.product_strategy,
      },
      competitor_analysis_view: {
        featureId: "competitor_analysis",
        service: services.competitor_analysis,
      },
    };

    const enabledFeatures = workflow.uiComponents?.enabledFeatures || [];
    const featureWorkflows = [];

    for (const feature of enabledFeatures) {
      const workflowInfo = featureToWorkflowMapping[feature];
      if (workflowInfo && workflowInfo.service) {
        featureWorkflows.push({
          featureId: feature,
          workflowId: workflowInfo.featureId,
          service: workflowInfo.service,
          steps: workflowInfo.service.getSteps(),
        });
      }
    }

    return featureWorkflows;
  }

  // Get the required feature workflows to run on initial plan creation
  // Configuration-driven: reads from uiComponents.autoRunFeatures
  getInitialPlanWorkflows(workflowId) {
    const workflow = this.getWorkflow(workflowId);
    if (!workflow) return [];

    // Read autoRunFeatures from product configuration
    const autoRunFeatures = workflow.uiComponents?.autoRunFeatures || [];

    if (autoRunFeatures.length === 0) {
      console.log(
        `📋 getInitialPlanWorkflows: No autoRunFeatures for ${workflowId}`,
      );
      return [];
    }

    // Map feature IDs to their workflow service keys
    const featureToServiceKey = {
      strategy_view: "brief",
      step_by_step_view: "action_plan",
      product_strategy_view: "product_strategy",
      competitor_analysis_view: "competitor_analysis",
    };

    // Convert feature IDs to service keys, maintaining order
    const serviceKeys = autoRunFeatures
      .map((featureId) => featureToServiceKey[featureId])
      .filter(Boolean);

    console.log(
      `📋 getInitialPlanWorkflows: ${workflowId} autoRun features:`,
      autoRunFeatures,
      "-> services:",
      serviceKeys,
    );

    return this.buildFeatureWorkflowList(serviceKeys);
  }

  // Helper: Build feature workflow list from feature IDs
  buildFeatureWorkflowList(featureIds) {
    console.log(
      "📋 buildFeatureWorkflowList: Building list for features:",
      featureIds,
    );

    const services = this.featureWorkflowServices;
    console.log(
      "📋 buildFeatureWorkflowList: Available service keys:",
      Object.keys(services),
    );

    const result = [];

    for (const featureId of featureIds) {
      const service = services[featureId];
      console.log(
        `📋 buildFeatureWorkflowList: Feature ${featureId} -> service:`,
        service ? "found" : "NOT FOUND",
      );

      if (service) {
        const steps = service.getSteps();
        console.log(
          `📋 buildFeatureWorkflowList: ${featureId} has ${steps.length} steps`,
        );
        result.push({
          featureId,
          service,
          steps,
        });
      } else {
        console.warn(
          `⚠️ WorkflowRegistry: No service found for feature ${featureId}`,
        );
      }
    }

    console.log(
      `📋 buildFeatureWorkflowList: Returning ${result.length} feature workflows`,
    );
    return result;
  }
}

// Create and export singleton instance
const workflowRegistryInstance = new WorkflowRegistry();

// Ensure clean export to prevent React component errors
export default workflowRegistryInstance;
